// Copyright (c) 2020, Picsa Advisory Services (Pty) Ltd. All rights reserved.

import React from 'react'
import PropTypes from 'prop-types'
import { Query } from 'react-apollo'
import gql from 'graphql-tag'

import {
  Spinner,
  SvgIcon,
  Warning,
  SMSSend
} from '../../'

import { getNodeTypeFromBase64Id } from '../../../utils'

import { CHANNEL_SMS_MESSAGEPLUS } from '../../../constants'

import { MessageList } from './MessageList'

const QUERY_SMS_MESSAGE = gql`
  query($messageId: ID!) {
    message(id: $messageId) {
      id
      direction
      fromIdentity {
        id
      }
      fromAddr
      toIdentity {
        id
      }
      toAddr
    }
  }
`

const QUERY_SMS_MESSAGES_BY_MSISDN = gql`
  query($msisdn: String!) {
    messages(search: $msisdn, orderBy: "created_at") {
      totalCount
      edges {
        node {
          id
          content
          direction
          messageType
          status
          createdAt
          fromIdentity {
            id
            fullName
          }
          fromAddr
          toIdentity {
            id
            fullName
          }
          toAddr
        }
      }
    }
  }
`

const QUERY_WHATSAPP_MESSAGE = gql`
  query($messageId: ID!) {
    message: whatsappmessage(id: $messageId) {
      id
      direction
      fromIdentity {
        id
        waId
      }
      fromWaId
      toIdentity {
        id
        waId
      }
      toWaId
    }
  }
`

const QUERY_WHATSAPP_MESSAGES_BY_MSISDN = gql`
  query($msisdn: String!) {
    messages: whatsappmessages(search: $msisdn, orderBy: "created_at") {
      totalCount
      edges {
        node {
          id
          body
          direction
          messageType
          status
          createdAt
          fromIdentity {
            id
            fullName
          }
          fromWaId
          toIdentity {
            id
            fullName
          }
          toWaId
        }
      }
    }
  }
`


class EmployerRelatedMessagesList extends React.PureComponent {
  static propTypes = {
    employerIdentityId: PropTypes.string.isRequired,
    title: PropTypes.string,
    initialMessageId: PropTypes.string.isRequired,
    currentMessageId: PropTypes.string.isRequired,
    changeMessageId: PropTypes.func.isRequired,
    className: PropTypes.string
  }

  static defaultProps = {
    className: ''
  }

  scrollIntoView = (id, refs, defaultScrollBehaviour = false) => {
    if (refs.hasOwnProperty(id)) {
      refs[id].current.scrollIntoView({
        behavior: defaultScrollBehaviour ? 'auto' : 'smooth',
        block: 'center',
      })
    }
  }

  render() {
    const { title, initialMessageId, currentMessageId, changeMessageId, className } = this.props
    const isSMSMessageNodeType = getNodeTypeFromBase64Id(initialMessageId) === "MessageNode"

    return (
      <div className={className} style={{ height: '86vh' }}>
        <div className='u-font-size-large slds-p-top_small slds-p-bottom_small slds-border_bottom slds-align_absolute-center'>
          {title
            ? title
            : isSMSMessageNodeType
              ? 'Related SMS messages'
              : 'Related WhatsApp messages'
          }
        </div>
        <div className='slds-scrollable section-details' style={{ height: '76vh' }}>
          <Query
            query={isSMSMessageNodeType
              ? QUERY_SMS_MESSAGE
              : QUERY_WHATSAPP_MESSAGE
            }
            variables={{ messageId: initialMessageId }}>
            {({ loading, error, data}) => {
              if (loading) {
                return (
                  <div className='slds-is-relative' style={{ height: '80vh' }}>
                    <Spinner type='tail' />
                  </div>
                )
              }

              if (error) {
                return (
                  <Warning className='slds-m-top_medium'>
                    Failed to fetch message msisdn.
                  </Warning>
                )
              }

              if (data.message === null) {
                return (
                  <Warning className='slds-m-top_medium'>
                    No WhatsApp message found.
                  </Warning>
                )
              }

              let msisdn = null
              if (isSMSMessageNodeType) {
                if (data.message.direction === "INBOUND") {
                  msisdn = data.message.fromAddr
                } else {
                  msisdn = data.message.toAddr
                }
              } else {
                if (data.message.direction === "INBOUND") {
                  msisdn = data.message.fromWaId
                } else {
                  msisdn = data.message.toWaId
                }
              }

              return (
                <Query
                  query={isSMSMessageNodeType
                    ? QUERY_SMS_MESSAGES_BY_MSISDN
                    : QUERY_WHATSAPP_MESSAGES_BY_MSISDN
                  }
                  variables={{ msisdn }}>

                  {({ loading, error, data, refetch }) => {
                    if (loading) {
                      return (
                        <div className='slds-is-relative' style={{ height: '80vh' }}>
                          <Spinner variant='brand' size='large' containerClassName='spinnerContainer' />
                        </div>
                      )
                    }

                    if (error) {
                      return (
                        <Warning>
                          Failed to fetch messages
                        </Warning>
                      )
                    }

                    let messages = data.messages.edges.map(message => message.node)

                    if (isSMSMessageNodeType) {
                      messages = messages.filter(message => (
                        ((message.direction === 'INBOUND' &&
                          message.toIdentity &&
                          message.toIdentity.id === this.props.employerIdentityId) ||
                          (message.direction === 'OUTBOUND' &&
                          message.fromIdentity &&
                          message.fromIdentity.id === this.props.employerIdentityId))
                      ))
                    }

                    let initialMessage = messages.find(message => message.id === currentMessageId)

                    const refs = messages.reduce((acc, message) => {
                      acc[message.id] = React.createRef()
                      return acc
                    }, {})

                    return (
                      <section role='log' className='chat'>
                        <div className="chat-bookend" style={{ border: 'none' }}>
                          <SvgIcon
                            variant='utility'
                            symbol='chat'
                            size='x-small'
                            colour='light'
                            className='slds-m-right_x-small'
                          />
                          <div className='slds-text-body_small slds-text-color_weak'>Start of message thread</div>
                        </div>
                        <MessageList
                          messages={messages}
                          selectedMessage={currentMessageId}
                          onMessageClick={changeMessageId}
                          scrollIntoView={this.scrollIntoView}
                          onMounted={() => this.scrollIntoView(initialMessage.id, refs, true)}
                          refs={refs}
                        />
                        <div className="chat-bookend chat-bookend_stop slds-m-top_small" style={{ border: 'none' }}>
                          <SvgIcon
                            variant='utility'
                            symbol='chat'
                            size='x-small'
                            colour='light'
                            className='slds-m-right_x-small'
                          />
                          <div className='slds-text-body_small slds-text-color_weak'>End of message thread</div>
                        </div>
                        {isSMSMessageNodeType &&
                          <div className='slds-grid slds-p-top_large'>
                            <SMSSend
                              toAddr={msisdn}
                              channelId={CHANNEL_SMS_MESSAGEPLUS}
                              fromIdentityId={this.props.employerIdentityId}
                              fromAddr={'*24220'}  // shortcode MessagePlus
                              onMessageCreateSuccess={() => setTimeout(refetch, 2000)}  // refresh after sms send
                              className='slds-size_2-of-3 slds-p-around_small slds-col_bump-left chat-message__text_outbound_compose'
                            />
                          </div>
                        }
                      </section>
                    )
                  }}
                </Query>
              )
            }}
          </Query>
        </div>
      </div>
    )
  }
}

export default EmployerRelatedMessagesList



// WEBPACK FOOTER //
// ./src/components/messageplus/employer-related-messages-list/index.js